import type { ApiClientDeps } from '@pay-roadrunner/api-client-core';
import { parseOrThrow } from '@pay-roadrunner/api-client-core';
import type {
  TaxConfigurationGroupDetailResponse,
  TaxConfigurationRequestError,
  TaxFinancialYearDto,
} from './taxConfigurationApiTypes';

const appendQuery = (path: string, query: Record<string, string | undefined>) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const text = params.toString();
  return text ? `${path}?${text}` : path;
};

export const isTaxConfigurationUnauthorized = (error: unknown): error is TaxConfigurationRequestError =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  ((error as TaxConfigurationRequestError).status === 401 || (error as TaxConfigurationRequestError).status === 403);

export const createTaxConfigurationClient = (adapter: ApiClientDeps) => {
  const fetchImpl = adapter.fetchImpl ?? fetch;
  const base = () => `${adapter.getBaseUrl()}/api/TaxConfiguration`;

  const fetchTaxFinancialYears = async (regionCode: string) => {
    const res = await fetchImpl(appendQuery(`${base()}/financial-years`, { regionCode }));
    return parseOrThrow<TaxFinancialYearDto[]>(res);
  };

  const fetchTaxConfigurationGroupDetails = async (groupKey: string, regionCode: string, financialYear: string) => {
    const res = await fetchImpl(appendQuery(`${base()}/groups/${encodeURIComponent(groupKey)}`, { regionCode, financialYear }));
    return parseOrThrow<TaxConfigurationGroupDetailResponse>(res);
  };

  return { fetchTaxFinancialYears, fetchTaxConfigurationGroupDetails };
};

export type TaxConfigurationClient = ReturnType<typeof createTaxConfigurationClient>;
